import { useToast } from '@contexts/Toast';
import { errorMessage } from '@helpers/Constants';
import { ApiEndpoints } from '@helpers/Constants/Navigation';
import {
  FeeType,
  FeeTypeForm,
  FeeTypeSheetProps,
  ServiceRateFeetypeSelection,
} from '@helpers/Models';
import { Axios, getErrorMessage } from '@helpers/ApiService';
import { useEffect, useState } from 'react';
import { faXmark, faPen, faSearch, faCheck } from '@fortawesome/free-solid-svg-icons';
import { faTrashCan } from '@fortawesome/free-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { Assets } from '@helpers/Constants/Assets';

// Override deletedAt to be optional string or null (matching API response)
type FeeTypeWithDeleted = Omit<FeeType, 'deletedAt'> & { deletedAt?: string | null };

export const FeeTypeBottomSheet: React.FC<FeeTypeSheetProps> = ({
  onClose,
  onSelect,
}) => {
  const { showToastMessage } = useToast();
  const [allFeeTypes, setAllFeeTypes] = useState<FeeTypeWithDeleted[]>([]);
  const [activeFeeTypes, setActiveFeeTypes] = useState<FeeTypeWithDeleted[]>([]);
  const [isAdding, setIsAdding] = useState(false);
  const [formData, setFormData] = useState<FeeTypeForm>({
    feeType: '',
    unit: '',
    availableFlag: true,
  });
  const [editFormData, setEditFormData] = useState<FeeTypeForm>({
    feeType: '',
    unit: '',
    availableFlag: false,
  });
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [selectedRow, setSelectedRow] = useState<ServiceRateFeetypeSelection>();
  const [searchKey, setSearchKey] = useState('');
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const UNIT_OPTIONS = ['inch', 'cm', 'foot'];

  // Helper to get only active (non‑deleted) items
  const getActive = (feeTypes: FeeTypeWithDeleted[]): FeeTypeWithDeleted[] =>
    feeTypes.filter((fee) => !fee.deletedAt);

  useEffect(() => {
    fetchFeeTypes();
  }, []);

  // Update active list whenever all list or search changes
  useEffect(() => {
    const active = getActive(allFeeTypes);
    if (!searchKey.trim()) {
      setActiveFeeTypes(active);
    } else {
      const filtered = active.filter(
        (feeType) =>
          feeType.feeType.toLowerCase().includes(searchKey.toLowerCase()) ||
          feeType.unit.toLowerCase().includes(searchKey.toLowerCase()),
      );
      setActiveFeeTypes(filtered);
    }
  }, [searchKey, allFeeTypes]);

  const fetchFeeTypes = async (): Promise<void> => {
    try {
      setIsLoading(true);
      const response = await Axios.post(ApiEndpoints.FETCH_FEETYPE, {
        searchKey: '',
      });
      const feeTypesData = response.data.data.feeTypes; // already have deletedAt as string or null
      setAllFeeTypes(feeTypesData);
      setIsLoading(false);
    } catch (error) {
      showToastMessage(errorMessage.ERROR_FETCHING_FEETYPES, 'error');
      setIsLoading(false);
    }
  };

  const handleRowClick = (feeType: ServiceRateFeetypeSelection): void => {
    setSelectedRow(feeType);
    onSelect(feeType);
    onClose();
  };

  const handleEdit = (index: number): void => {
    setEditingIndex(index);
    const fee = activeFeeTypes[index];
    setEditFormData({
      feeType: fee.feeType,
      unit: fee.unit,
      availableFlag: fee.availableFlag,
    });
  };

  const handleAddFeeType = async (): Promise<void> => {
    setErrors({});

    if (!formData.feeType.trim() || !formData.unit.trim()) {
      setErrors({
        feeType: formData.feeType.trim() ? '' : errorMessage.FIELD_REQUIRED,
        unit: formData.unit.trim() ? '' : errorMessage.FIELD_REQUIRED,
      });
      return;
    }

    const trimmedFee = formData.feeType.trim();
    const trimmedUnit = formData.unit.trim();

    // Find any matching item (including deleted) with same feeType and unit (case‑insensitive)
    const duplicate = allFeeTypes.find(
      (fee) =>
        fee.feeType.trim().toLowerCase() === trimmedFee.toLowerCase() &&
        fee.unit.trim().toLowerCase() === trimmedUnit.toLowerCase(),
    );

    if (duplicate) {
      if (!duplicate.deletedAt) {
        // Active duplicate → show error
        setErrors({
          isDuplicate: errorMessage.FEETYPE_DUPLICATE_ERROR,
        });
        return;
      } else {
        // Deleted duplicate → restore it
        try {
          await Axios.post(ApiEndpoints.RESTORE_FEETYPE, { ids: [duplicate.id] });
          // Update local state: set deletedAt to null for that item
          const updatedAll = allFeeTypes.map((fee) =>
            fee.id === duplicate.id ? { ...fee, deletedAt: null } : fee,
          );
          setAllFeeTypes(updatedAll);
          setIsAdding(false);
          setFormData({ feeType: '', unit: '', availableFlag: true });
          showToastMessage('Fee type restored successfully', 'success');
        } catch (error) {
          showToastMessage(getErrorMessage(error), 'error');
        }
        setErrors({});
        return;
      }
    }

    // No duplicate at all → proceed with upsert
    const payload = {
      ...formData,
      availableFlag: formData.availableFlag ? 1 : 0,
      flatFeeFlag: 0,
    };
    try {
      const response = await Axios.post(ApiEndpoints.UPSERT_FEETYPE, [payload]);
      const newItems = response.data.data; // each has deletedAt: null
      setAllFeeTypes((prev) => [...prev, ...newItems]);
      setIsAdding(false);
      setFormData({ feeType: '', unit: '', availableFlag: true });
      showToastMessage(response.data.message, 'success');
    } catch (error) {
      showToastMessage(getErrorMessage(error), 'error');
    }
    setErrors({});
  };

  const handleDelete = async (id: number | undefined): Promise<void> => {
    try {
      if (id !== undefined) {
        await Axios.delete(ApiEndpoints.DELETE_FEETYPE, {
          data: { ids: [id] },
        });
      }
      // Mark item as deleted locally with timestamp (string)
      setAllFeeTypes((prev) =>
        prev.map((fee) =>
          fee.id === id ? { ...fee, deletedAt: new Date().toISOString() } : fee,
        ),
      );
    } catch (error) {
      showToastMessage(errorMessage.FETCH_ERROR, 'error');
    }
  };

  const handleEditChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>): void => {
    const { name, value, type, checked } = e.target as HTMLInputElement;

    setEditFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSaveEdit = async (): Promise<void> => {
    if (!editFormData.feeType.trim() || !editFormData.unit.trim()) {
      setErrors({
        feeType: editFormData.feeType.trim() ? '' : errorMessage.FIELD_REQUIRED,
        unit: editFormData.unit.trim() ? '' : errorMessage.FIELD_REQUIRED,
      });
      return;
    }
    if (editingIndex === null) return;
    setErrors({});

    // Update the item in the full list
    const updatedAll = allFeeTypes.map((fee) => {
      if (fee.id === activeFeeTypes[editingIndex].id) {
        return { ...fee, ...editFormData };
      }
      return fee;
    });
    setAllFeeTypes(updatedAll);
    setEditingIndex(null);

    // Prepare payload for all active items (we don't send deleted ones)
    const payload = getActive(updatedAll).map((fee) => ({
      ...(fee.id && { id: fee.id }),
      feeType: fee.feeType,
      unit: fee.unit,
      availableFlag: fee.availableFlag ? 1 : 0,
    }));

    try {
      const response = await Axios.post(ApiEndpoints.UPSERT_FEETYPE, payload);
      // Replace the full list with the response (which should contain all items)
      setAllFeeTypes(response.data.data);
    } catch (error) {
      showToastMessage(getErrorMessage(error), 'error');
    }
    setErrors({});
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>): void => {
    const { name, value, type, checked } = e.target as HTMLInputElement;

    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const ActionButton: React.FC<{
    icon: IconDefinition;
    onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
    disabled: boolean;
  }> = ({ icon, onClick, disabled }) => (
    <button
      type="button"
      onClick={(e) => {
        if (!disabled) {
          onClick(e);
        }
      }}
      disabled={disabled}
      className={`p-1 ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
    >
      <FontAwesomeIcon icon={icon} />
    </button>
  );

  const handleAddOrCancel = (key?: string): void => {
    setFormData({ feeType: '', unit: '', availableFlag: true });
    switch (key) {
      case 'add':
        setIsAdding(true);
        break;
      case 'cancel':
        setErrors({});
        setIsAdding(false);
        break;
    }
  };

  return (
    <>
      {!isLoading && (
        <div className="fixed z-10000 inset-0 bg-black bg-opacity-40 flex justify-center items-center w-full h-full p-4 animate-fadeIn">
          <div className="bg-white rounded-2xl w-full max-w-[650px] max-h-[90vh] shadow-lg flex flex-col animate-slideUpFade">
            {/* Header */}
            <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200">
              <h1 className="font-semibold text-lg">Add Fee Type</h1>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => handleAddOrCancel('add')}
                  type="button"
                  className="px-3 py-1 border border-transparent rounded-sm text-sm font-medium text-white bg-primary hover:bg-dark-primary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                >
                  + Add More
                </button>
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search..."
                    onChange={(e) => setSearchKey(e.target.value)}
                    value={searchKey}
                    className="border border-gray-300 rounded px-3 py-1 h-8 w-60 text-sm focus:outline-primary"
                  />
                  <FontAwesomeIcon
                    icon={faSearch}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500"
                  />
                </div>
                <button
                  onClick={onClose}
                  type="button"
                  className="text-gray-600 hover:text-gray-900 text-xl"
                >
                  ✕
                </button>
              </div>
            </div>

            {/* Scrollable content */}
            <div className="overflow-y-auto flex-1 px-6 py-4">
              <table className="w-full table-fixed border-collapse">
                <thead className="bg-gray-100 sticky top-0 z-10">
                  <tr className="border-b">
                    <th className="w-[30%] px-4 py-3 text-left">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold">Title</span>
                        <img
                          src={Assets.SORT_ICON}
                          alt="Sort"
                          className="h-3 w-3"
                        />
                      </div>
                    </th>
                    <th className="w-[25%] px-4 py-3 text-left">
                      <span className="text-sm font-semibold">Units</span>
                    </th>
                    <th className="w-[25%] px-4 py-3 text-right"></th>
                  </tr>
                </thead>
                <tbody>
                  {activeFeeTypes.length > 0 ? (
                    activeFeeTypes.map((feeType: FeeTypeWithDeleted, index: number) => (
                      <tr
                        key={feeType.id ?? index}
                        className={`cursor-pointer transition ${selectedRow?.id === feeType.id
                            ? 'bg-tertiary border-primary'
                            : 'hover:bg-gray-100'
                          }`}
                        onClick={(e) => {
                          if (
                            editingIndex !== index &&
                            !(e.target instanceof HTMLInputElement) &&
                            !(e.target instanceof HTMLSelectElement)
                          ) {
                            handleRowClick({
                              id: feeType.id ?? 0,
                              feeType: feeType.feeType,
                              unit: feeType.unit,
                            });
                          }
                        }}
                      >
                        <td className="px-4 py-3 align-middle">
                          {editingIndex === index ? (
                            <div>
                              <input
                                type="text"
                                name="feeType"
                                value={editFormData.feeType}
                                onChange={handleEditChange}
                                className="w-full border-b-2 border-gray-300 focus:border-primary-500 outline-none bg-transparent"
                              />
                              {errors.feeType && (
                                <p className="text-red-500 text-xs mt-1">
                                  {errors.feeType}
                                </p>
                              )}
                            </div>
                          ) : (
                            <span className="text-sm font-medium">
                              {feeType.feeType}
                            </span>
                          )}
                        </td>

                        <td className="px-4 py-3 align-middle">
                          {editingIndex === index ? (
                            <div>
                              <select
                                name="unit"
                                value={editFormData.unit}
                                onChange={handleEditChange}
                                className="w-full border-b-2 border-gray-300 focus:border-primary-500 outline-none bg-transparent"
                              >
                                <option value="">Select unit</option>
                                {UNIT_OPTIONS.map((unit) => (
                                  <option key={unit} value={unit}>
                                    {unit}
                                  </option>
                                ))}
                              </select>
                              {errors.unit && (
                                <p className="text-red-500 text-xs mt-1">
                                  {errors.unit}
                                </p>
                              )}
                            </div>
                          ) : (
                            <span className="text-sm font-medium">
                              {feeType.unit}
                            </span>
                          )}
                        </td>

                        <td className="px-4 py-3 text-right align-middle">
                          <div className="flex justify-end gap-3">
                            {editingIndex === index ? (
                              <>
                                <button type="button" onClick={handleSaveEdit}>
                                  <FontAwesomeIcon
                                    icon={faCheck}
                                    className="text-primary text-[22px]"
                                  />
                                </button>
                                <ActionButton
                                  icon={faXmark}
                                  onClick={() => setEditingIndex(null)}
                                  disabled={false}
                                />
                              </>
                            ) : (
                              <>
                                <ActionButton
                                  icon={faPen}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleEdit(index);
                                  }}
                                  disabled={feeType.flatFeeFlag === 1}
                                />
                                <ActionButton
                                  icon={faTrashCan}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDelete(feeType.id);
                                  }}
                                  disabled={feeType.flatFeeFlag === 1}
                                />
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={3} className="text-center p-4">
                        No data available
                      </td>
                    </tr>
                  )}
                  {isAdding && (
                    <tr>
                      <td className="px-4 py-3">
                        <input
                          type="text"
                          name="feeType"
                          placeholder="New Feetype"
                          value={formData.feeType}
                          autoFocus
                          onChange={handleInputChange}
                          className="w-full border-b-2 border-gray-300 focus:border-primary-500 outline-none bg-transparent"
                        />
                        {errors.feeType && (
                          <p className="text-red-500 text-xs mt-1">
                            {errors.feeType}
                          </p>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <select
                          name="unit"
                          value={formData.unit}
                          onChange={handleInputChange}
                          className="w-full border-b-2 border-gray-300 focus:border-primary-500 outline-none bg-transparent"
                        >
                          <option value="">Select unit</option>
                          {UNIT_OPTIONS.map((unit) => (
                            <option key={unit} value={unit}>
                              {unit}
                            </option>
                          ))}
                        </select>
                        {errors.unit && (
                          <p className="text-red-500 text-xs mt-1">
                            {errors.unit}
                          </p>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex justify-end gap-3">
                          <button
                            type="button"
                            onClick={handleAddFeeType}
                            className="p-1"
                          >
                            <FontAwesomeIcon
                              icon={faCheck}
                              className="text-primary text-[22px]"
                            />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleAddOrCancel('cancel')}
                            className="p-1"
                          >
                            <FontAwesomeIcon
                              icon={faXmark}
                              className="text-gray-600 text-[22px]"
                            />
                          </button>
                        </div>
                      </td>
                    </tr>
                  )}
                  {errors.isDuplicate && (
                    <tr>
                      <td colSpan={3}>
                        <p className="text-red-500 text-xs mt-1">
                          {errors.isDuplicate}
                        </p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUpFade {
          from {
            opacity: 0;
            transform: translateY(30px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.2s ease-out forwards;
        }
        .animate-slideUpFade {
          animation: slideUpFade 0.25s ease-out forwards;
        }
      `}</style>
    </>
  );
};